import { BaseClient } from '../BaseClient';
import {
  CallWindowLimitDate,
  CallWindowLimitDateBulkRequest,
  CallWindowLimitDateEntry,
  CallWindowLimitDateList,
  CallWindowLimitDateRangeRequest,
} from '../compiled-protos/call_window_limit_date';
import { CallWindowLimitDateServiceClient } from '../compiled-protos/call_window_limit_date.client';
import { Empty } from '../compiled-protos/common';

// Every method here bypasses the backend's response memoizer via getMetaDataWithoutCache().
// kalos-core's auth UnaryInterceptor memoizes each unary response for 5 seconds, keyed on
// method + request, unless the request carries a cache-control header. The dialog reads a date
// range, deletes an override, then refetches that same range well inside 5s -- the refetch was
// served the pre-delete snapshot, so a removed date stayed on screen until the entry expired.
// The writes opt out for the same reason: an identical bulk upsert or delete repeated inside
// the window would return the first call's response without ever reaching the database.
class CallWindowLimitDateClient extends BaseClient {
  self: CallWindowLimitDateServiceClient;

  constructor(host: string, userID?: number) {
    super(host, userID);
    this.self = new CallWindowLimitDateServiceClient(this.transport);
  }

  public async Create(req: CallWindowLimitDate): Promise<CallWindowLimitDate | undefined> {
    let res = CallWindowLimitDate.create();
    try {
      res = await this.self.createCallWindowLimitDate(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async Get(req: CallWindowLimitDate): Promise<CallWindowLimitDate | undefined> {
    let res = CallWindowLimitDate.create();
    try {
      res = await this.self.getCallWindowLimitDate(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGet(req: CallWindowLimitDate): Promise<CallWindowLimitDateList | undefined> {
    let res = CallWindowLimitDateList.create();
    try {
      res = await this.self.batchGetCallWindowLimitDate(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async Update(req: CallWindowLimitDate): Promise<CallWindowLimitDate | undefined> {
    let res = CallWindowLimitDate.create();
    try {
      res = await this.self.updateCallWindowLimitDate(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async Delete(req: CallWindowLimitDate): Promise<Empty | undefined> {
    let res = Empty.create();
    try {
      res = await this.self.deleteCallWindowLimitDate(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BatchGetRange(
    req: CallWindowLimitDateRangeRequest,
  ): Promise<CallWindowLimitDateList | undefined> {
    let res = CallWindowLimitDateList.create();
    try {
      res = await this.self.batchGetCallWindowLimitDateRange(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BulkUpsert(
    req: CallWindowLimitDateBulkRequest,
  ): Promise<CallWindowLimitDateList | undefined> {
    let res = CallWindowLimitDateList.create();
    try {
      res = await this.self.bulkUpsertCallWindowLimitDate(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  public async BulkDelete(req: CallWindowLimitDateBulkRequest): Promise<Empty | undefined> {
    let res = Empty.create();
    try {
      res = await this.self.bulkDeleteCallWindowLimitDate(req, this.getMetaDataWithoutCache()).response;
    } catch (err) {
      console.log(err);
      return;
    }
    return res;
  }

  // Helper method to load the date overrides falling inside an inclusive date range.
  public loadCallWindowLimitDates = async ({
    filter,
  }: LoadCallWindowLimitDatesRequest): Promise<CallWindowLimitDateList> => {
    const req = CallWindowLimitDateRangeRequest.create();
    req.dateStart = filter.dateStart;
    req.dateEnd = filter.dateEnd;
    req.departmentId = filter.departmentId ?? 0;
    req.eventTradeId = filter.eventTradeId ?? 0;

    const result = await this.BatchGetRange(req);
    // BatchGetRange swallows the RPC error and resolves undefined. Returning an empty list here
    // made a failed read indistinguishable from "this range has no overrides", so callers
    // rendered a transport failure as "no date limits" and the rows came back on the next good
    // read. Reject instead and let the caller show the failure.
    if (!result) throw new Error('Failed to load call window date limits');
    return result;
  };
}

export {
  CallWindowLimitDateClient,
  CallWindowLimitDate,
  CallWindowLimitDateBulkRequest,
  CallWindowLimitDateEntry,
  CallWindowLimitDateList,
  CallWindowLimitDateRangeRequest,
};

export type LoadCallWindowLimitDatesRequest = {
  filter: CallWindowLimitDateFilter;
};

export type CallWindowLimitDateFilter = {
  // Inclusive, yyyy-MM-dd.
  dateStart: string;
  dateEnd: string;
  // 0 or omitted returns every department's overrides -- department 0 is the shared/global
  // bucket, whose windows never carry an override.
  departmentId?: number;
  // 0 or omitted returns every trade's overrides.
  eventTradeId?: number;
};
